/**
 * Admin API Service
 * Module 10: Admin Dashboard
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const API_URL = API_BASE_URL;
console.log('Admin-changes')

export const adminService = {
  /**
   * Agent Traces
   */
  async getAgentTraces(filters: {
    userId?: string;
    questionId?: string;
    startDate?: string;
    endDate?: string;
    success?: boolean;
    limit?: number;
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/admin/agent-traces`, {
      headers: { Authorization: `Bearer ${token}` },
      params: filters
    });
    return response.data;
  },

  async getAgentTraceById(traceId: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/admin/agent-traces/${traceId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * MCP Servers
   */
  async getMCPServerHealth() {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/admin/mcp-servers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  async restartMCPServer(serverName: string) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_URL}/admin/mcp-servers/${serverName}/restart`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Analytics
   */
  async getCostAnalytics(token: string, startDate?: string, endDate?: string) {
    const response = await axios.get(`${API_URL}/admin/analytics/costs`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { startDate, endDate }
    });
    return response.data;
  },

  async getPipelinePerformance(token: string) {
    const response = await axios.get(`${API_URL}/admin/analytics/pipeline-performance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * Generation Sessions
   */
  async getGenerationSessions(filters: {
    userId?: string;
    status?: string;
    limit?: number;
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_URL}/admin/generation-sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: filters
    });
    return response.data;
  },

  /**
   * System Stats
   */
  async getSystemStats(token: string) {
    const response = await axios.get(`${API_URL}/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * User Management
   */
  async getAllUsers(token: string, filters?: {
    role?: string;
    isActive?: boolean;
    limit?: number;
    skip?: number;
  }) {
    const response = await axios.get(`${API_URL}/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
      params: filters || {}
    });
    return response.data.data; // Return just the users array
  },

  async createUser(data: any, token: string) {
    const response = await axios.post(
      `${API_URL}/admin/users`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async updateUser(userId: string, updates: any, token: string) {
    const response = await axios.put(
      `${API_URL}/admin/users/${userId}`,
      updates,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async deleteUser(userId: string, token: string) {
    const response = await axios.delete(`${API_URL}/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * Role Management
   */
  async getAllRoles(token: string) {
    const response = await axios.get(`${API_URL}/admin/roles`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  async createRole(data: any, token: string) {
    const response = await axios.post(
      `${API_URL}/admin/roles`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async deleteRole(roleId: string, token: string) {
    const response = await axios.delete(`${API_URL}/admin/roles/${roleId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * System Configuration
   */
  async getSystemConfig(token: string) {
    const response = await axios.get(`${API_URL}/admin/system-config`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  async updateSystemConfig(data: any, token: string) {
    const response = await axios.put(
      `${API_URL}/admin/system-config`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Dropdown Configuration
   */
  async getDropdownConfig(category: string, token: string) {
    const response = await axios.get(`${API_URL}/admin/dropdown-config/${category}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  async createDropdownConfig(data: any, token: string) {
    const response = await axios.post(
      `${API_URL}/admin/dropdown-config`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async updateDropdownConfig(id: string, data: any, token: string) {
    const response = await axios.put(
      `${API_URL}/admin/dropdown-config/${id}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async deleteDropdownConfig(id: string, token: string) {
    const response = await axios.delete(`${API_URL}/admin/dropdown-config/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * Question Type Configuration
   */
  async updateQuestionTypeConfig(templates: any, token: string) {
    const response = await axios.put(
      `${API_URL}/admin/question-type-config`,
      { templates },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Question Management
   */
  async reviewQuestion(questionId: string, data: {
    status: 'approved' | 'rejected';
    reviewNotes?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_URL}/admin/questions/${questionId}/review`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async bulkQuestionOperation(operation: {
    questionIds: string[];
    operation: 'approve' | 'reject' | 'delete';
    notes?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_URL}/admin/questions/bulk-operation`,
      operation,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }
};
